/**
 * Learning Moments → Taxonomy
 *
 * The induced hierarchy from an offline run: broad parent families, each with
 * the topic-scoped children beneath it. Read-only — nothing here is authored or
 * promoted; this is the review surface that decides what eventually becomes a
 * learning moment.
 *
 * What to judge:
 *  - a parent is useful only if a student could act on it. "Demonstrate
 *    Conceptual Understanding" cannot be acted on and should not survive.
 *  - a child earns its place by being MORE specific than its parent. Children
 *    that merely restate the family per topic are flagged `echoesParent`.
 *  - concept-pair children ("Distinguish X from Y") are the class the examiner
 *    corpus says we are missing — see docs/pedagogy/cisce-examiner-reports.md.
 */

import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSubjectLabel } from '@shared-ui/reference-data';
import { fetchWithAdmin } from '../../api/fetchHelper';
import { LearningMomentTabs } from './LearningMomentTabs';
import './InsightTaxonomy.css';

interface Exemplar { text: string; count: number }

interface Parent {
  key: string;
  label: string;
  description: string;
  scope: string | null;
  itemCount: number;
  distinctPhrases: number;
  subjectCount: number;
  topicCount: number;
  subjects: { subject: string; n: number }[];
  topTopics: { topic: string; n: number }[];
  exemplars: Exemplar[];
  childCount: number;
  childItemCount: number;
}

interface Child {
  key: string;
  parentKey: string;
  label: string;
  description: string;
  scope: string;
  subject: string | null;
  topic: string;
  itemCount: number;
  distinctPhrases: number;
  exemplars: Exemplar[];
  echoesParent: boolean;
}

interface RunSummary { runId: string; source: string; itemCount: number }

const isConceptPair = (label: string) => /^distinguish\b/i.test(label);

export function InsightTaxonomy() {
  const [runId, setRunId] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [hideEchoes, setHideEchoes] = useState(false);
  const subjectLabel = useSubjectLabel();

  const runs = useQuery({
    queryKey: ['insight-runs'],
    queryFn: () => fetchWithAdmin<{ runs: RunSummary[] }>('/console/insight-runs'),
  });
  const activeRun = runId || runs.data?.runs[0]?.runId || '';

  const tax = useQuery({
    queryKey: ['insight-taxonomy', activeRun],
    queryFn: () => fetchWithAdmin<{ parents: Parent[]; children: Child[] }>(
      `/console/insight-runs/${activeRun}/taxonomy`,
    ),
    enabled: !!activeRun,
  });

  const parents = tax.data?.parents ?? [];
  const allChildren = tax.data?.children ?? [];
  const children = hideEchoes ? allChildren.filter(c => !c.echoesParent) : allChildren;
  const byParent = new Map<string, Child[]>();
  for (const c of children) {
    if (!byParent.has(c.parentKey)) byParent.set(c.parentKey, []);
    byParent.get(c.parentKey)!.push(c);
  }

  const pairs = allChildren.filter(c => isConceptPair(c.label));
  const echoes = allChildren.filter(c => c.echoesParent);

  return (
    <div className="lm-page">
      <div className="lm-header"><h1>Learning Moments</h1></div>
      <LearningMomentTabs active="taxonomy" />

      <p className="tx-intro">
        The taxonomy induced from a run — broad families, each with the topic-scoped children
        beneath it. Nothing here is live; this is the review surface for deciding what becomes
        a learning moment. A parent earns its place only if a student could act on it; a child
        earns its place by being <em>more specific</em> than its parent.
      </p>

      {runs.data && runs.data.runs.length > 0 && (
        <div className="tx-controls">
          <select value={activeRun} onChange={e => { setRunId(e.target.value); setExpanded(null); }} aria-label="Run">
            {runs.data.runs.map(r => (
              <option key={r.runId} value={r.runId}>{r.runId} · {r.source}</option>
            ))}
          </select>
          <label className="check-label">
            <input type="checkbox" checked={hideEchoes} onChange={e => setHideEchoes(e.target.checked)} />
            <span title="Children whose label merely restates their parent, adding nothing over it">
              Hide parent-echo children
            </span>
          </label>
        </div>
      )}

      {tax.isLoading && <p>Loading taxonomy…</p>}
      {tax.isError && <p className="tx-error">Failed: {(tax.error as Error).message}</p>}
      {tax.data && parents.length === 0 && (
        <p className="lm-empty">
          No taxonomy published for this run. Generate one with{' '}
          <code>induce.cjs</code> then <code>publish-taxonomy.cjs --apply</code>.
        </p>
      )}

      {tax.data && parents.length > 0 && (
        <>
          <div className="tx-stats">
            <span><strong>{parents.length}</strong> parents</span>
            <span><strong>{allChildren.length}</strong> children</span>
            <span className="tx-good" title="Children naming a confusion between two ideas — the class the examiner corpus says we lack">
              <strong>{pairs.length}</strong> concept-pairs
            </span>
            <span className="tx-warn" title="Children that merely restate their parent per topic">
              <strong>{echoes.length}</strong> parent-echoes
            </span>
          </div>

          <table className="dt tx-table">
            <thead>
              <tr>
                <th>Family / child</th>
                <th className="tx-num">Items</th>
                <th className="tx-num">Subjects</th>
                <th className="tx-num">Topics</th>
                <th>Scope</th>
              </tr>
            </thead>
            <tbody>
              {parents.map(p => {
                const kids = byParent.get(p.key) ?? [];
                const open = expanded === p.key;
                return (
                  <Fragment key={p.key}>
                    <tr
                      className={`tx-parent ${open ? 'tx-open' : ''}`}
                      onClick={() => setExpanded(open ? null : p.key)}
                    >
                      <td>
                        <div className="tx-plabel">
                          <span className="tx-caret">{open ? '▾' : '▸'}</span>
                          {p.label}
                          {kids.length > 0 && <span className="tx-kidcount">{kids.length} children</span>}
                        </div>
                        <div className="tx-desc">{p.description}</div>
                      </td>
                      <td className="tx-num">{p.itemCount.toLocaleString()}</td>
                      <td className="tx-num">{p.subjectCount}</td>
                      <td className="tx-num">{p.topicCount}</td>
                      <td><span className={`tx-scope tx-scope-${p.scope ?? 'none'}`}>{p.scope ?? '—'}</span></td>
                    </tr>

                    {open && (
                      <tr className="tx-detail">
                        <td colSpan={5}>
                          <div className="tx-drillrow">
                            <Link
                              className="tx-drill tx-drill-btn"
                              to={`/learning-moments/taxonomy/${activeRun}/parent/${encodeURIComponent(p.key)}`}
                            >
                              Read the evidence — {p.itemCount.toLocaleString()} items →
                            </Link>
                          </div>
                          {p.exemplars.length > 0 && (
                            <div className="tx-block">
                              <div className="tx-blocktitle">Phrases that named this family</div>
                              <ul className="tx-phrases">
                                {p.exemplars.map((e, i) => (
                                  <li key={i}><span className="tx-count">{e.count}×</span> {e.text}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {kids.length === 0
                            ? <div className="tx-none">No children — this family has no topic-specific detail.</div>
                            : (
                              <div className="tx-block">
                                <div className="tx-blocktitle">Children (module-scoped)</div>
                                <table className="dt tx-kids">
                                  <tbody>
                                    {kids.map(c => (
                                      <tr key={c.key} className={c.echoesParent ? 'tx-echo' : ''}>
                                        <td>
                                          <div className="tx-clabel">
                                            {isConceptPair(c.label) && <span className="tx-pair" title="Concept-pair — the class the examiner corpus says we lack">pair</span>}
                                            {c.echoesParent && <span className="tx-echotag" title="Restates the parent; adds nothing over it">echoes parent</span>}
                                            {c.label}
                                          </div>
                                          <ul className="tx-phrases tx-phrases-sm">
                                            {c.exemplars.slice(0, 3).map((e, i) => (
                                              <li key={i}><span className="tx-count">{e.count}×</span> {e.text}</li>
                                            ))}
                                          </ul>
                                        </td>
                                        <td className="tx-num tx-kidnum">
                                          <Link
                                            className="tx-drill"
                                            to={`/learning-moments/taxonomy/${activeRun}/child/${encodeURIComponent(c.key)}`}
                                            onClick={e => e.stopPropagation()}
                                            title="Read the student work behind this cluster"
                                          >
                                            {c.itemCount}
                                          </Link>
                                        </td>
                                        <td className="tx-kidtopic">
                                          {c.subject ? subjectLabel(c.subject) : ''}
                                          <div className="tx-topic">{c.topic}</div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
